import { toIntlLocale } from "./locale";

/**
 * Language and region names, from Intl and never from a table.
 *
 * The language control is a word, never a flag: a flag names a country and a language is not
 * one, and the 32-locale list the shared checkout is growing needs no table if the names come
 * from the runtime (TECH.md, "Language and currency are words, not flags").
 */

/**
 * The language written in its own words: "Français", "Deutsch", "English".
 *
 * The BARE language, never the region. The detector hands back what the browser claims, which is
 * usually a full tag, and `Intl.DisplayNames` answers a full tag with the dialect: "en-US" came
 * back as "American English" on the live page. A person choosing a language is choosing a
 * language, so the region is dropped before the name is asked for.
 */
export function languageOwnName(code: string): string {
  const base = code.split("-")[0];
  try {
    const names = new Intl.DisplayNames([base], { type: "language" });
    const name = names.of(base);
    if (!name) return base.toUpperCase();
    // Every language capitalises its own name at the head of a control label; French and Italian
    // write theirs lower case in a sentence, which reads as a typo on a button.
    return name.charAt(0).toLocaleUpperCase(toIntlLocale(base)) + name.slice(1);
  } catch {
    return base.toUpperCase();
  }
}

/** A country's name in the reader's language, for the billing address select. */
export function regionName(code: string, lang: string): string {
  try {
    const names = new Intl.DisplayNames([toIntlLocale(lang)], {
      type: "region",
    });
    return names.of(code.toUpperCase()) ?? code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

/**
 * The ISO 3166-1 alpha-2 codes Stripe accepts, sorted by their name in the reader's language.
 * The list is the runtime's own (`Intl.supportedValuesOf("region")` where it exists), so no
 * country table is maintained here; the fallback is the set Stripe Connect actually supports.
 */
const FALLBACK_REGIONS = [
  "AE",
  "AT",
  "AU",
  "BE",
  "BG",
  "BR",
  "CA",
  "CH",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HK",
  "HR",
  "HU",
  "IE",
  "IT",
  "JP",
  "LT",
  "LU",
  "LV",
  "MT",
  "MX",
  "MY",
  "NL",
  "NO",
  "NZ",
  "PL",
  "PT",
  "RO",
  "SE",
  "SG",
  "SI",
  "SK",
  "TH",
  "US",
];

export function regionOptions(
  lang: string,
): Array<{ code: string; name: string }> {
  let codes: string[] = FALLBACK_REGIONS;
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supported === "function") {
    try {
      const all = supported("region").filter((code) => /^[A-Z]{2}$/.test(code));
      if (all.length > 0) codes = all;
    } catch {
      // The runtime does not know the key; the Stripe list below is the honest fallback.
    }
  }
  const collator = new Intl.Collator(toIntlLocale(lang));
  return codes
    .map((code) => ({ code, name: regionName(code, lang) }))
    .sort((a, b) => collator.compare(a.name, b.name));
}
